<template>
    <div class="l-headline">
        <div class="row">
            <div :class="[hasSearchBar ? 'col-xs-8' : 'col-md-10 col-xs-12']">
                <span
                    v-b-popover.hover.top="{
                        content: titlePopover,
                        disabled: !titlePopover,
                    }"
                    class="headline tw-line-clamp-1"
                    data-test-id="title"
                >
                    {{ title }}
                </span>
                <i v-if="titleIcon" class="l-default-margin-s" :class="titleIcon"></i>
            </div>

            <div v-if="hasSearchBar" class="col-xs-4">
                <l-input-text
                    v-model="search"
                    style="margin-top: 0px; margin-bottom: -20px"
                    type="text"
                    :label="searchInputLabel"
                    :placeholder="searchInputPlaceholder"
                    hide-help-block
                    optional
                    @input="value => $emit('search', value)"
                />
            </div>
        </div>
        <div
            :class="[
                'material-tabs',
                { 'has-btn': btnShow, 'has-content': tabs.length || subTitle },
            ]"
        >
            <!-- Subtitle -->
            <a :class="['descriptive', { 'is-empty': !subTitle }]" data-test-id="subtitle">
                <i v-if="subTitleIcon" class="r-default-margin-s" :class="subTitleIcon"></i>
                {{ subTitle }}
            </a>
            <!--/Subtitle -->

            <!-- autoSaveText -->
            <a v-if="autoSaveText != ''" class="descriptive">
                <i class="fa fa-refresh fa-spin"></i>
                <span class="l-default-margin-s">{{ autoSaveText }}</span>
            </a>
            <!-- autoSaveText -->

            <!-- To ensure the button is all the way to the write when subtitle, we need to create an artificial space element -->
            <!-- Note: As tabs might have other separations, `space` needs to be specified within the `tabs` array -->
            <span v-if="subTitle && btnShow" class="space"></span>

            <!-- Navigation Tabs-->
            <template v-for="(tab, index) in tabs">
                <a
                    v-if="tab.show && !tab.isButton"
                    :key="tab.label || index"
                    v-b-popover.hover.top="{ content: tab.popover, disabled: !tab.popover }"
                    :class="[{ active: tab.active }, tab.tabClass]"
                    :href="`#${tab.path || ''}`"
                    :target="tab.openNewWindow ? `_blank` : ''"
                    rel="noopener noreferrer"
                    :data-test-id="
                        tab.testId ||
                            (tab.path &&
                                tab.path
                                    .split('?')[0]
                                    .replace('/', 'menu-')
                                    .replace(/\//g, '-'))
                    "
                    @click="
                        e => {
                            if (tab.handler) {
                                e.preventDefault();
                                tab.handler();
                            }

                            if (!tab.path) {
                                e.preventDefault();
                            }
                        }
                    "
                >
                    <i
                        v-if="tab.icon && tab.iconBeforeLabel"
                        class="r-default-margin-s"
                        :class="tab.icon"
                    ></i
                    >{{ tab.label
                    }}<i
                        v-if="tab.icon && !tab.iconBeforeLabel"
                        class="l-default-margin-s"
                        :class="tab.icon"
                    ></i>
                    <span v-if="tab.active" class="material-tabs-bar"></span>
                </a>
                <button
                    v-else-if="tab.show && tab.isButton"
                    :key="tab.label || index"
                    v-b-popover.hover.top="{ content: tab.popover, disabled: !tab.popover }"
                    class="btn btn-sm btn-raised l-headline-btn"
                    :class="[{ active: tab.active }, tab.tabClass]"
                    :data-test-id="tab.testId"
                    @click="handleButton(tab)"
                >
                    <i
                        v-if="tab.icon && tab.iconBeforeLabel"
                        class="r-default-margin-s"
                        :class="tab.icon"
                    ></i
                    >{{ tab.label
                    }}<i
                        v-if="tab.icon && !tab.iconBeforeLabel"
                        class="l-default-margin-s"
                        :class="tab.icon"
                    ></i>
                    <span v-if="tab.active" class="material-tabs-bar"></span>
                </button>
            </template>
            <!--/Navigation Tabs-->
        </div>
    </div>
</template>

<script>
import LInputText from "@/components/LInputText";

export default {
    components: {
        LInputText,
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        titlePopover: {
            type: String,
            default: "",
        },
        titleIcon: {
            type: String,
            default: "",
        },
        subTitle: {
            type: String,
            default: "",
        },
        subTitleIcon: {
            type: String,
            default: "",
        },
        autoSaveText: {
            type: String,
            default: "",
        },
        btnShow: {
            type: Boolean,
            default: false,
        },
        btnHandler: {
            type: Function,
            default: () => {},
        },
        btnIcon: {
            type: String,
            default: "",
        },
        btnText: {
            type: String,
            default: "",
        },
        btnTestId: {
            type: String,
            default: "l-headline-btn",
        },
        tabs: {
            type: Array,
            default: () => [],
        },
        hasSearchBar: {
            type: Boolean,
            default: false,
        },
        searchInputLabel: {
            type: String,
            default: "",
        },
        searchInputPlaceholder: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            search: "",
        };
    },
    methods: {
        handleButton(tab) {
            if (tab.handler) {
                tab.handler();
            } else if (tab.path) {
                this.$router.push(tab.path);
            }
        },
    },
};
</script>

<style scoped>
.l-headline .descriptive.is-empty {
    padding: 0;
}

/* Only display flex when there is content, as we want the space under the title to be present even if there are no
 * subtitles (which requires `display: block`).
 *
 * We use flexbox here to ensure the space between the subtitle/tabs and the button (if present).
 *
 */
.l-headline .material-tabs.has-content {
    display: flex;
}
.l-headling .space,
.l-headline >>> .space {
    flex-grow: 1;
    cursor: default;
}

.l-headline .descriptive,
.l-headline >>> .descriptive {
    cursor: default;
}

.l-headline .btn-icon {
    margin-right: 10px;
}
.l-headline-btn {
    cursor: pointer;
    display: flex;
    align-items: center;
}

.hover\:tw-underline:hover {
    text-decoration: underline;
}
</style>
